import { StorefrontOutlined } from '@mui/icons-material';
import { Stack } from '@mui/material';
import { useState } from 'react';

import AssessmentBillingCard from './assessmentBillingCard';
import BillingRuleDialog from './billingRuleDialog';
import DefaultRuleCard from './defaultRuleCard';
import ServicesBillingTable from './servicesBillingTable';
import ConfirmationDialog from 'components/v2/common/confirmationDialog';
import Result from 'components/v2/common/Result';

import { ACTIONS } from 'constant';
import {
    countServicesOnDefault,
    getEditableRule,
    toBillingRuleFormValues,
} from 'helpers/serviceProviderBilling';
import useModalObj from 'hooks/useModalObj';
import useTranslate from 'hooks/useTranslate';
import type { BillingModalData, BillingRule, ServiceCountryCode } from 'types';
import { localizedServiceName } from 'utils/servicesDocuments';
import { useServiceProviderBilling } from 'views/v2/serviceProviderBilling/hooks/useServiceProviderBilling';

const MODAL_COPY: Record<
    ACTIONS.EDIT | ACTIONS.DELETE,
    Record<BillingModalData['target'], { title: string; content: string }>
> = {
    [ACTIONS.EDIT]: {
        default: {
            title: 'serviceProviderBilling.dialog.defaultTitle',
            content: 'serviceProviderBilling.dialog.defaultDescription',
        },
        services: {
            title: 'serviceProviderBilling.dialog.customTitle',
            content: 'serviceProviderBilling.dialog.customDescription',
        },
    },
    [ACTIONS.DELETE]: {
        default: {
            title: 'serviceProviderBilling.removeDefaultTitle',
            content: 'serviceProviderBilling.removeDefaultContent',
        },
        services: {
            title: 'serviceProviderBilling.resetTitle',
            content: 'serviceProviderBilling.resetContent',
        },
    },
};

interface BillingSetupPanelProps {
    providerId: number | null;
    countryCode: ServiceCountryCode | null;
    /** Provider and country, shown under the dialog titles. */
    scopeLabel: string;
}

const BillingSetupPanel = ({ providerId, countryCode, scopeLabel }: BillingSetupPanelProps) => {
    const { translate, language } = useTranslate();
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const { services, config, loading, saveDefault, removeDefault, saveOverrides, resetOverrides } =
        useServiceProviderBilling(providerId, countryCode);
    const { modalObject, setModalObject, onResetModalObj } = useModalObj<BillingModalData>();

    const handleAction = (action: ACTIONS, data: BillingModalData) => {
        if (action !== ACTIONS.EDIT && action !== ACTIONS.DELETE) {
            return;
        }
        const copy = MODAL_COPY[action][data.target];
        setModalObject((prev) => ({
            ...prev,
            action,
            data,
            title: translate(copy.title),
            content: translate(copy.content),
            disabled: false,
            loading: false,
        }));
    };

    const runModalAction = async (request: (data: BillingModalData) => Promise<boolean>) => {
        const { data } = modalObject;
        if (!data) {
            return;
        }
        setModalObject((prev) => ({ ...prev, loading: true }));
        if (!(await request(data))) {
            setModalObject((prev) => ({ ...prev, loading: false }));
            return;
        }
        if (data.target === 'services') {
            setSelectedIds((ids) => ids.filter((id) => !data.serviceIds.includes(id)));
        }
        onResetModalObj();
    };

    const handleSubmitRule = (rule: BillingRule) =>
        runModalAction((data) =>
            data.target === 'default' ? saveDefault(rule) : saveOverrides(data.serviceIds, rule)
        );

    const handleConfirmDelete = () =>
        runModalAction((data) =>
            data.target === 'default' ? removeDefault() : resetOverrides(data.serviceIds)
        );

    const getDialogSubtitle = (data: BillingModalData) => {
        if (data.target === 'default') {
            return scopeLabel;
        }
        if (data.serviceIds.length === 1) {
            const service = services.find((item) => item.id === data.serviceIds[0]);
            return service
                ? `${localizedServiceName(service, language)} · ${scopeLabel}`
                : scopeLabel;
        }
        return `${data.serviceIds.length} ${translate(
            'serviceProviderBilling.dialog.servicesSelected'
        )} · ${scopeLabel}`;
    };

    if (providerId == null || countryCode == null) {
        return (
            <Result
                icon={<StorefrontOutlined sx={{ fontSize: 32 }} />}
                title={translate('serviceProviderBilling.selectProviderTitle')}
                description={translate('serviceProviderBilling.selectProviderDescription')}
            />
        );
    }

    return (
        <Stack spacing={2.5}>
            <AssessmentBillingCard providerId={providerId} />
            <DefaultRuleCard
                loading={loading}
                rule={config?.default_rule ?? null}
                servicesOnDefault={countServicesOnDefault(services, config)}
                servicesTotal={services.length}
                handleAction={handleAction}
            />
            <ServicesBillingTable
                loading={loading}
                services={services}
                config={config}
                language={language}
                selectedIds={selectedIds}
                onSelectionChange={setSelectedIds}
                handleAction={handleAction}
            />

            {modalObject.action === ACTIONS.EDIT && modalObject.data ? (
                <BillingRuleDialog
                    title={modalObject.title}
                    subtitle={getDialogSubtitle(modalObject.data)}
                    description={modalObject.content}
                    initialValues={toBillingRuleFormValues(
                        getEditableRule(modalObject.data, config),
                        countryCode
                    )}
                    loading={modalObject.loading}
                    onClose={onResetModalObj}
                    onSubmit={handleSubmitRule}
                />
            ) : null}

            {modalObject.action === ACTIONS.DELETE ? (
                <ConfirmationDialog
                    title={modalObject.title}
                    content={modalObject.content}
                    loading={modalObject.loading}
                    onClose={onResetModalObj}
                    onConfirm={handleConfirmDelete}
                />
            ) : null}
        </Stack>
    );
};

export default BillingSetupPanel;
